import { Aircraft, Transaction, Event, Company } from '../../../types/intellijetnet'
import { AircraftOverride, AircraftTransaction, Option, TransactionOverride, MarketCustomColumn, MarketEMP, TakePriceModifier } from '../../../types/database'
import type { EditingCell, EditingCellFieldName } from '../types'
import type { EffectiveAircraft, EffectiveTransactionAircraft } from '../../../hooks/useMarketTableData'
import type { RelativeValueBreakdown } from '../../../hooks/useRelativeValue'
import { OffMarket } from '../../../types/database'
import { ConsolidatedTransaction } from '../../../utils/transaction-consolidation'

/**
 * Shared props passed to all cell components from MarketTableRow.
 * Keeps individual cell component interfaces small by referencing this base.
 */
export interface CellBaseProps {
  aircraft: Aircraft
  tableType: 'for-sale' | 'sold' | 'removed'
  isEditMode?: boolean
  cellBorderClass: string
  effectiveAircraft: EffectiveAircraft | EffectiveTransactionAircraft

  // Override data
  overrides: Map<number, AircraftOverride>
  transactionOverrides: Map<string, TransactionOverride>
  aircraftTransaction?: AircraftTransaction | null

  // Editing state
  editingCell: EditingCell | null

  // Editing handlers
  handleStartEdit: (aircraftId: number, fieldName: EditingCellFieldName) => void
  handleStartTransactionEdit: (aircraftId: number, transactionId: string, fieldName: EditingCellFieldName) => void
  handleCancelEdit: () => void
  handleFieldSave: (aircraftId: number, fieldName: EditingCellFieldName, newValue: string | number | null) => Promise<void>
  handleTransactionFieldSave: (transactionId: string, fieldName: EditingCellFieldName, newValue: string | number | null) => Promise<void>
}

/**
 * Resolves whether we're in "sold with transaction" mode, and returns
 * the transaction override + editability flag. This pattern is repeated
 * in virtually every cell component.
 */
export function resolveOverrideContext(props: CellBaseProps) {
  const { tableType, aircraftTransaction, transactionOverrides, aircraft, overrides, isEditMode } = props
  const isSoldWithTransaction = tableType === 'sold' && !!aircraftTransaction
  const isEditable = !!isEditMode && (tableType !== 'sold' || isSoldWithTransaction)
  const transOverride = isSoldWithTransaction ? transactionOverrides.get(aircraftTransaction!.transaction_id) : null
  const override = overrides.get(aircraft.aircraft_id)

  return { isSoldWithTransaction, isEditable, transOverride, override }
}

/**
 * Checks if a specific field is currently being edited for this aircraft/transaction.
 */
export function isFieldEditing(props: CellBaseProps, fieldName: EditingCellFieldName, isSoldWithTransaction: boolean): boolean {
  const { editingCell, aircraft, aircraftTransaction } = props
  if (!editingCell) return false

  if (isSoldWithTransaction) {
    return editingCell.transactionId === aircraftTransaction!.transaction_id && editingCell.fieldName === fieldName
  }
  return editingCell.aircraftId === aircraft.aircraft_id && !editingCell.transactionId && editingCell.fieldName === fieldName
}

/**
 * Creates a click handler that starts editing for a field.
 * Returns undefined if not editable (to be used as onClick={handler}).
 */
export function makeEditClickHandler(
  props: CellBaseProps,
  fieldName: EditingCellFieldName,
  isEditable: boolean,
  isSoldWithTransaction: boolean
): ((e: React.MouseEvent) => void) | undefined {
  if (!isEditable) return undefined
  const { aircraft, aircraftTransaction, handleStartEdit, handleStartTransactionEdit } = props
  return (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isSoldWithTransaction) {
      handleStartTransactionEdit(aircraft.aircraft_id, aircraftTransaction!.transaction_id, fieldName)
    } else {
      handleStartEdit(aircraft.aircraft_id, fieldName)
    }
  }
}

/**
 * Creates a save handler that dispatches to the correct save function
 * based on whether we're editing an aircraft override or transaction override.
 */
export function makeFieldSaveHandler(
  props: CellBaseProps,
  fieldName: EditingCellFieldName,
  isSoldWithTransaction: boolean
): (newValue: string | number | null) => Promise<void> {
  const { aircraft, aircraftTransaction, handleFieldSave, handleTransactionFieldSave } = props
  return (newValue: string | number | null) => {
    if (isSoldWithTransaction) {
      return handleTransactionFieldSave(aircraftTransaction!.transaction_id, fieldName, newValue)
    }
    return handleFieldSave(aircraft.aircraft_id, fieldName, newValue)
  }
}

/**
 * CSS class for override highlight (red text) + editable hover state.
 */
export function overrideEditableClass(hasOverride: boolean, isEditable: boolean): string {
  return `${hasOverride ? 'text-red-800 font-medium' : ''} ${isEditable ? 'cursor-pointer hover:bg-green-50 rounded px-1 -mx-1' : ''}`
}

